import React from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEye,
    faKey,
    faPen,
    faPlus,
    faPowerOff,
    faRefresh,
    faUserTag
} from '@fortawesome/free-solid-svg-icons';
import Table from '../../Components/Table/Table';
import ConfirmationModal from '../../Components/ConfirmationModal/ConfirmationModal';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import { UsersDataService } from '../../Services/UsersDataService';
import { RolesDataService } from '../../Services/RolesDataService';
import { useSecurity } from '../../Contexts/SecurityContext';
import { formatDateTime } from '../../Utils/formaters';
import './UsersAdminPage.scss';
import UserPermissionsModal from './UserPermissionsModal';
import { Permissions } from '../../Security/permissions';

const apiErrorMessage = (error, fallback = 'The request could not be completed.') => {
    const problem = error?.response?.data;
    if (typeof problem === 'string') return problem;
    return problem?.detail || problem?.title || problem?.message || error?.message || fallback;
};

const blankUser = {
    email: '',
    displayName: '',
    isActive: true,
    entraObjectId: null,
    supplierId: null,
    roleId: null
};

const UserFormModal = ({ user, onHide, onSave }) => {
    const isEditing = Boolean(user?.userId);
    const [form, setForm] = React.useState(blankUser);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');
    const [validated, setValidated] = React.useState(false);

    React.useEffect(() => {
        setForm(user ? { ...blankUser, ...user } : blankUser);
        setError('');
        setValidated(false);
    }, [user]);

    const change = (field, value) => setForm(current => ({ ...current, [field]: value }));

    const submit = async event => {
        event.preventDefault();
        const htmlForm = event.currentTarget;
        setValidated(true);
        if (!htmlForm.checkValidity()) return;

        setSaving(true);
        setError('');
        try {
            await onSave({
                entraObjectId: form.entraObjectId || null,
                email: form.email.trim(),
                displayName: form.displayName.trim() || null,
                ...(isEditing ? {} : { isActive: form.isActive }),
                supplierId: form.supplierId ?? null,
                roleId: form.roleId ?? null
            });
        } catch (saveError) {
            setError(apiErrorMessage(saveError, 'The user could not be saved.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show onHide={saving ? undefined : onHide} backdrop={saving ? 'static' : true} centered>
            <Form noValidate validated={validated} onSubmit={submit}>
                <Modal.Header closeButton={!saving}>
                    <Modal.Title>{isEditing ? 'Edit user' : 'Register user'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <Form.Group className="mb-3" controlId="app-user-display-name">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            maxLength={256}
                            value={form.displayName}
                            onChange={event => change('displayName', event.target.value)}
                            placeholder="User display name"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="app-user-email">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                            required
                            type="email"
                            maxLength={500}
                            value={form.email}
                            onChange={event => change('email', event.target.value)}
                            placeholder="name@company.com"
                        />
                        <Form.Control.Feedback type="invalid">Enter a valid email address.</Form.Control.Feedback>
                        {/* <Form.Text muted>
                            The application uses this email as a fallback until the Microsoft account is linked.
                        </Form.Text> */}
                    </Form.Group>
                    {!isEditing && (
                        <Form.Check
                            type="switch"
                            id="app-user-active"
                            label="Allow application access immediately"
                            checked={form.isActive}
                            onChange={event => change('isActive', event.target.checked)}
                        />
                    )}
                    {isEditing && form.roleName && (
                        <div className="users-admin-readonly mt-3">
                            <span className="text-muted small">Current application role</span>
                            <strong>{form.roleName}</strong>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
                    <Button type="submit" disabled={saving}>
                        {saving ? <><Spinner animation="border" size="sm" className="me-2" />Saving...</> : 'Save user'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

const UserDetailsModal = ({ state, onHide }) => {
    const user = state?.user;
    return (
        <Modal show onHide={onHide} centered>
            <Modal.Header closeButton><Modal.Title>User details</Modal.Title></Modal.Header>
            <Modal.Body>
                {state?.loading && <div className="users-admin-state"><Spinner animation="border" /><span>Loading user...</span></div>}
                {state?.error && <Alert variant="danger" className="mb-0">{state.error}</Alert>}
                {user && (
                    <dl className="users-admin-details mb-0">
                        <div><dt>Name</dt><dd>{user.displayName || 'Not provided'}</dd></div>
                        <div><dt>Email</dt><dd>{user.email}</dd></div>
                        <div><dt>Status</dt><dd><StatusBadge active={user.isActive} /></dd></div>
                        {user.roleName && <div><dt>Role</dt><dd>{user.roleName}</dd></div>}
                        <div><dt>Application user ID</dt><dd>{user.userId}</dd></div>
                        <div><dt>Registered</dt><dd>{formatDateTime(user.createdAtUtc) || 'Not available'}</dd></div>
                        {user.updatedAtUtc && <div><dt>Last updated</dt><dd>{formatDateTime(user.updatedAtUtc)}</dd></div>}
                    </dl>
                )}
                {user && (
                    <Alert variant="light" className="small mt-4 mb-0">
                        Action-level and company access will be added in later security phases.
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer><Button variant="secondary" onClick={onHide}>Close</Button></Modal.Footer>
        </Modal>
    );
};

export const UserRoleModal = ({ user, roles, onHide, onSave }) => {
    const [roleId, setRoleId] = React.useState(user.roleId == null ? '' : String(user.roleId));
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');
    const assignedRoleIsUnavailable = user.roleId != null
        && !roles.some(role => role.roleId === user.roleId);

    const submit = async event => {
        event.preventDefault();
        setSaving(true);
        setError('');
        try {
            await onSave(roleId === '' ? null : Number(roleId));
        } catch (saveError) {
            setError(apiErrorMessage(saveError, 'The user role could not be changed.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show onHide={saving ? undefined : onHide} backdrop={saving ? 'static' : true} centered>
            <Form onSubmit={submit}>
                <Modal.Header closeButton={!saving}><Modal.Title>Assign role</Modal.Title></Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <p className="text-muted small">
                        Choose an active role for {user.displayName || user.email}, or clear the role assignment.
                    </p>
                    <Form.Group controlId={`app-user-role-${user.userId}`}>
                        <Form.Label>Role</Form.Label>
                        <Form.Select value={roleId} onChange={event => setRoleId(event.target.value)}>
                            <option value="">No role</option>
                            {assignedRoleIsUnavailable && (
                                <option value={String(user.roleId)} disabled>
                                    {user.roleName || `Role ${user.roleId}`} (inactive or unavailable)
                                </option>
                            )}
                            {roles.map(role => (
                                <option key={role.roleId} value={String(role.roleId)}>{role.roleName || `Role ${role.roleId}`}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
                    <Button type="submit" disabled={saving || roleId === (user.roleId == null ? '' : String(user.roleId))}>
                        {saving ? <><Spinner animation="border" size="sm" className="me-2" />Saving...</> : 'Save role'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

const StatusBadge = ({ active }) => (
    <Badge bg={active ? 'success' : 'secondary'}>{active ? 'Active' : 'Inactive'}</Badge>
);

const UsersAdminContent = () => {
    const { appUser, acquireAccessToken, refreshSecurity, canExecute } = useSecurity();
    const readOnly = !canExecute(Permissions.Security.Users);
    const [users, setUsers] = React.useState([]);
    const [roles, setRoles] = React.useState([]);
    const [search, setSearch] = React.useState('');
    const [includeInactive, setIncludeInactive] = React.useState(true);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [success, setSuccess] = React.useState('');
    const [editing, setEditing] = React.useState(undefined);
    const [detail, setDetail] = React.useState(null);
    const [statusChange, setStatusChange] = React.useState(null);
    const [statusSaving, setStatusSaving] = React.useState(false);
    const [permissionsUser, setPermissionsUser] = React.useState(null);
    const [roleUser, setRoleUser] = React.useState(null);

    const loadUsers = React.useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const token = await acquireAccessToken();
            const [loadedUsers, loadedRoles] = await Promise.all([
                UsersDataService.getUsers(token, includeInactive),
                RolesDataService.getRoles(token, false)
            ]);
            setUsers(loadedUsers);
            setRoles(loadedRoles);
        } catch (loadError) {
            setError(apiErrorMessage(loadError, 'Users could not be loaded.'));
        } finally {
            setLoading(false);
        }
    }, [acquireAccessToken, includeInactive]);

    React.useEffect(() => { loadUsers(); }, [loadUsers]);

    const saveUser = async body => {
        if (readOnly) return;
        const token = await acquireAccessToken();
        if (editing?.userId) {
            await UsersDataService.updateUser(editing.userId, body, token);
            setSuccess('User updated successfully.');
        } else {
            await UsersDataService.createUser(body, token);
            setSuccess('User registered successfully.');
        }
        setEditing(undefined);
        if (editing?.userId === appUser?.userId) refreshSecurity();
        else await loadUsers();
    };

    const viewUser = React.useCallback(async user => {
        setDetail({ loading: true, error: '', user: null });
        try {
            const token = await acquireAccessToken();
            const result = await UsersDataService.getUser(user.userId, token);
            setDetail({ loading: false, error: '', user: result });
        } catch (viewError) {
            setDetail({ loading: false, error: apiErrorMessage(viewError, 'User details could not be loaded.'), user: null });
        }
    }, [acquireAccessToken]);

    const applyStatusChange = async () => {
        if (readOnly) return;
        if (!statusChange) return;
        setStatusSaving(true);
        setError('');
        try {
            const newStatus = !statusChange.isActive;
            await UsersDataService.setUserStatus(statusChange.userId, newStatus, await acquireAccessToken());
            setSuccess(`User ${newStatus ? 'activated' : 'deactivated'} successfully.`);
            setStatusChange(null);
            if (statusChange.userId === appUser?.userId) refreshSecurity();
            else await loadUsers();
        } catch (statusError) {
            setError(apiErrorMessage(statusError, 'The user status could not be changed.'));
            setStatusChange(null);
        } finally {
            setStatusSaving(false);
        }
    };

    const saveUserRole = async roleId => {
        if (readOnly) return;
        const user = roleUser;
        if (!user) return;
        await UsersDataService.setUserRole(user.userId, roleId, await acquireAccessToken());
        setSuccess('User role updated successfully.');
        setRoleUser(null);
        if (user.userId === appUser?.userId) refreshSecurity();
        else await loadUsers();
    };

    const visibleUsers = React.useMemo(() => {
        const value = search.trim().toLowerCase();
        if (!value) return users;
        return users.filter(user => [user.displayName, user.email, user.roleName]
            .some(field => String(field || '').toLowerCase().includes(value)));
    }, [search, users]);

    const columns = React.useMemo(() => [
        {
            header: 'Name',
            accessorKey: 'displayName',
            cell: ({ row }) => row.original.displayName || <span className="text-muted">Not provided</span>
        },
        { header: 'Email', accessorKey: 'email' },
        {
            header: 'Role',
            accessorKey: 'roleName',
            cell: ({ getValue }) => getValue() || <span className="text-muted">Standard user</span>
        },
        { header: 'Status', accessorKey: 'isActive', cell: ({ getValue }) => <StatusBadge active={getValue()} /> },
        {
            header: 'Actions',
            id: 'actions',
            enableSorting: false,
            cell: ({ row }) => {
                const user = row.original;
                return (
                    <div className="users-admin-actions">
                        <Button size="sm" variant="outline-primary" title="View user" aria-label={`View ${user.email}`} onClick={() => viewUser(user)}>
                            <FontAwesomeIcon icon={faEye} />
                        </Button>
                        <Button disabled={readOnly} size="sm" variant="outline-secondary" title="Edit user" aria-label={`Edit ${user.email}`} onClick={() => setEditing(user)}>
                            <FontAwesomeIcon icon={faPen} />
                        </Button>
                        <Button size="sm" variant="outline-primary" title="Manage permissions" aria-label={`Manage permissions for ${user.email}`} onClick={() => setPermissionsUser(user)}>
                            <FontAwesomeIcon icon={faKey} />
                        </Button>
                        <Button disabled={readOnly} size="sm" variant="outline-secondary" title="Assign role" aria-label={`Assign role to ${user.email}`} onClick={() => setRoleUser(user)}>
                            <FontAwesomeIcon icon={faUserTag} />
                        </Button>
                        <Button
                            size="sm"
                            variant={user.isActive ? 'outline-danger' : 'outline-success'}
                            title={user.isActive ? 'Deactivate user' : 'Activate user'}
                            aria-label={`${user.isActive ? 'Deactivate' : 'Activate'} ${user.email}`}
                            onClick={() => setStatusChange(user)}
                            disabled={readOnly}
                        >
                            <FontAwesomeIcon icon={faPowerOff} />
                        </Button>
                    </div>
                );
            }
        }
    ], [viewUser, readOnly]);

    return (
        <div className="users-admin-page">
            {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}
            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

            <div className="users-admin-toolbar">
                <Form.Control
                    type="search"
                    aria-label="Search users"
                    placeholder="Search by name, email or role..."
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                />
                <Form.Check
                    type="switch"
                    id="include-inactive-users"
                    label="Include inactive"
                    checked={includeInactive}
                    onChange={event => setIncludeInactive(event.target.checked)}
                />
                <Button variant="outline-primary" title="Refresh" aria-label="Refresh users" onClick={loadUsers} disabled={loading}>
                    <FontAwesomeIcon icon={faRefresh} />
                </Button>
                <Button disabled={readOnly} onClick={() => setEditing(null)}>
                    <FontAwesomeIcon icon={faPlus} className="me-1" />Register user
                </Button>
            </div>

            {loading ? (
                <div className="users-admin-state"><Spinner animation="border" /><span>Loading users...</span></div>
            ) : visibleUsers.length ? (
                <Table
                    columns={columns}
                    data={visibleUsers}
                    tableClass="table-hover"
                    headerClass="table-light"
                    containerClass="table-responsive"
                />
            ) : (
                <Alert variant="light" className="users-admin-state">No users found.</Alert>
            )}

            {editing !== undefined && <UserFormModal user={editing} onHide={() => setEditing(undefined)} onSave={saveUser} />}
            {detail && <UserDetailsModal state={detail} onHide={() => setDetail(null)} />}
            {permissionsUser && (
                <UserPermissionsModal
                    user={permissionsUser}
                    onHide={() => setPermissionsUser(null)}
                    onSaved={() => {
                        setPermissionsUser(null);
                        setSuccess('User permissions updated successfully.');
                    }}
                />
            )}
            {roleUser && (
                <UserRoleModal
                    user={roleUser}
                    roles={roles}
                    onHide={() => setRoleUser(null)}
                    onSave={saveUserRole}
                />
            )}
            {statusChange && (
                <ConfirmationModal
                    show
                    title={`${statusChange.isActive ? 'Deactivate' : 'Activate'} user`}
                    content={`Are you sure you want to ${statusChange.isActive ? 'deactivate' : 'activate'} ${statusChange.displayName || statusChange.email}?`}
                    acceptText={statusSaving ? 'Saving...' : (statusChange.isActive ? 'Deactivate' : 'Activate')}
                    acceptVariant={statusChange.isActive ? 'danger' : 'success'}
                    acceptDisabled={statusSaving}
                    cancelDisabled={statusSaving}
                    closeDisabled={statusSaving}
                    onHide={() => setStatusChange(null)}
                    onCancelClick={() => setStatusChange(null)}
                    onAcceptClick={applyStatusChange}
                />
            )}
        </div>
    );
};

const UsersAdminPage = () => (
    <PageLayout2 title="User Administration">
        <UsersAdminContent />
    </PageLayout2>
);

export { UsersAdminContent };
export default UsersAdminPage;
